import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useScroll } from '@react-three/drei'
import * as THREE from 'three'

// Detect mobile for responsive sizing
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
const mobileScale = isMobile ? 0.7 : 1

// Bitcount-style font - each character is 5 columns of 7 dots
// Each entry: column positions that have dots (0-based from bottom)
const BITCOUNT_FONT = {
    S: {
        width: 5,
        dots: [
            [0, 5, 6],           // col 0: bottom corner + top bar
            [0, 4, 6],           // col 1: bottom + upper-left curve
            [0, 3, 6],           // col 2: all three crossings
            [0, 2, 6],           // col 3: lower-right curve + top
            [0, 1, 6],           // col 4: top corner + bottom bar
        ]
    },
    I: {
        width: 3,
        dots: [
            [0, 6],              // col 0: serifs
            [0, 1, 2, 3, 4, 5, 6], // col 1: vertical stem
            [0, 6],              // col 2: serifs
        ]
    },
    M: {
        width: 5,
        dots: [
            [0, 1, 2, 3, 4, 5, 6], // col 0: left stem
            [4, 5],              // col 1: left diagonal
            [3],                 // col 2: center point
            [4, 5],              // col 3: right diagonal
            [0, 1, 2, 3, 4, 5, 6], // col 4: right stem
        ]
    },
    P: {
        width: 5,
        dots: [
            [0, 1, 2, 3, 4, 5, 6], // col 0: vertical stem
            [3, 6],              // col 1
            [3, 6],              // col 2: bowl
            [3, 6],              // col 3
            [4, 5],              // col 4: bowl curve
        ]
    },
    L: {
        width: 5,
        dots: [
            [0, 1, 2, 3, 4, 5, 6], // col 0: vertical stem
            [0],                 // col 1
            [0],                 // col 2: bottom bar
            [0],                 // col 3
            [0],                 // col 4
        ]
    },
    E: {
        width: 5,
        dots: [
            [0, 1, 2, 3, 4, 5, 6], // col 0: vertical stem
            [0, 3, 6],           // col 1
            [0, 3, 6],           // col 2: three bars
            [0, 3, 6],           // col 3
            [0, 6],              // col 4: top and bottom only
        ]
    },
    D: {
        width: 5,
        dots: [
            [0, 1, 2, 3, 4, 5, 6], // col 0: vertical stem
            [0, 6],              // col 1
            [0, 6],              // col 2: top/bottom
            [1, 5],              // col 3: curve
            [2, 3, 4],           // col 4: right curve
        ]
    },
    O: {
        width: 5,
        dots: [
            [1, 2, 3, 4, 5],     // col 0: left curve
            [0, 6],              // col 1
            [0, 6],              // col 2: top/bottom
            [0, 6],              // col 3
            [1, 2, 3, 4, 5],     // col 4: right curve
        ]
    },
    T: {
        width: 5,
        dots: [
            [6],                 // col 0: top bar
            [6],                 // col 1
            [0, 1, 2, 3, 4, 5, 6], // col 2: vertical stem
            [6],                 // col 3
            [6],                 // col 4: top bar
        ]
    },
    ' ': {
        width: 2,
        dots: [[], []]
    },
}

// Generate particle positions from text
function generateTextParticles(text, dotSize = 0.12, dotSpacing = 0.14, charSpacing = 0.25) {
    const particles = []
    let offsetX = 0

    // Calculate total width for centering
    let totalWidth = 0
    for (const char of text) {
        const glyph = BITCOUNT_FONT[char] || BITCOUNT_FONT[' ']
        totalWidth += glyph.width * dotSpacing + charSpacing
    }
    const startX = -totalWidth / 2

    for (const char of text) {
        const glyph = BITCOUNT_FONT[char] || BITCOUNT_FONT[' ']

        for (let col = 0; col < glyph.width; col++) {
            const dots = glyph.dots[col] || []
            for (const row of dots) {
                const cx = startX + offsetX + col * dotSpacing
                const cy = (row - 3) * dotSpacing

                // Add dispersion target (random direction for break-apart effect)
                const disperseAngle = Math.random() * Math.PI * 2
                const disperseDistance = 3 + Math.random() * 5

                particles.push({
                    x: cx,
                    y: cy,
                    z: 0,
                    dotX: cx,
                    // Dispersion targets
                    disperseX: Math.cos(disperseAngle) * disperseDistance,
                    disperseY: Math.sin(disperseAngle) * disperseDistance + 2,
                    disperseZ: (Math.random() - 0.5) * 4,
                })
            }
        }

        offsetX += glyph.width * dotSpacing + charSpacing
    }

    return { particles, width: totalWidth }
}

export default function ParticleText({ text = 'SIMPLE DOTS', position = [0, 2, 0] }) {
    const points = useRef()
    const time = useRef(0)
    const scroll = useScroll()

    const [positions, colors, sizes, originalPositions, disperseTargets, width] = useMemo(() => {
        // Apply mobile scaling to text dimensions
        const { particles, width } = generateTextParticles(text, 0.12 * mobileScale, 0.14 * mobileScale, 0.25 * mobileScale)
        const count = particles.length

        const positions = new Float32Array(count * 3)
        const colors = new Float32Array(count * 3)
        const sizes = new Float32Array(count)
        const originalPositions = new Float32Array(count * 3)
        const disperseTargets = new Float32Array(count * 3)

        // Blue → Red gradient (left to right)
        const colorCyan = new THREE.Color('#00f3ff')
        const colorYellow = new THREE.Color('#fbbf24')
        const colorOrange = new THREE.Color('#f97316')

        particles.forEach((p, i) => {
            const i3 = i * 3

            positions[i3] = p.x
            positions[i3 + 1] = p.y
            positions[i3 + 2] = p.z

            originalPositions[i3] = p.x
            originalPositions[i3 + 1] = p.y
            originalPositions[i3 + 2] = p.z

            disperseTargets[i3] = p.disperseX
            disperseTargets[i3 + 1] = p.disperseY
            disperseTargets[i3 + 2] = p.disperseZ

            // Color based on horizontal position (blue to red)
            const t = Math.max(0, Math.min(1, (p.dotX + width / 2) / width))
            let color
            if (t < 0.5) {
                color = colorCyan.clone().lerp(colorYellow, t * 2)
            } else {
                color = colorYellow.clone().lerp(colorOrange, (t - 0.5) * 2)
            }

            colors[i3] = color.r
            colors[i3 + 1] = color.g
            colors[i3 + 2] = color.b

            // Smaller particles on mobile
            sizes[i] = (isMobile ? 1.5 : 2.5) + Math.random() * 0.5
        })

        return [positions, colors, sizes, originalPositions, disperseTargets, width]
    }, [text])

    useFrame((state, delta) => {
        if (!points.current) return

        time.current += delta

        // Get scroll progress (0 = top, 1 = bottom)
        const scrollProgress = scroll.offset

        // Start dispersing after 15% scroll, complete by 40%
        const disperseStart = 0.12
        const disperseEnd = 0.35
        const disperseAmount = Math.max(0, Math.min(1,
            (scrollProgress - disperseStart) / (disperseEnd - disperseStart)
        ))

        const posAttr = points.current.geometry.attributes.position
        const sizeAttr = points.current.geometry.attributes.size
        const count = posAttr.count

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            const ox = originalPositions[i3]
            const oy = originalPositions[i3 + 1]
            const oz = originalPositions[i3 + 2]

            const dx = disperseTargets[i3]
            const dy = disperseTargets[i3 + 1]
            const dz = disperseTargets[i3 + 2]

            // Interpolate between original position and disperse target
            const eased = disperseAmount * disperseAmount * (3 - 2 * disperseAmount) // smoothstep

            const phase = i * 0.015
            const baseX = ox + (dx - ox) * eased
            const baseY = oy + (dy - oy) * eased - scrollProgress * 3 // Move up as scroll
            const baseZ = oz + (dz - oz) * eased

            // Add subtle floating animation (reduced when dispersed)
            const floatScale = 1 - eased * 0.8
            posAttr.array[i3] = baseX + Math.sin(time.current * 0.25 + phase) * 0.006 * floatScale
            posAttr.array[i3 + 1] = baseY + Math.cos(time.current * 0.35 + phase) * 0.008 * floatScale
            posAttr.array[i3 + 2] = baseZ + Math.sin(time.current * 0.2 + phase) * 0.004 * floatScale

            // Fade out size as particles disperse
            sizeAttr.array[i] = (2.5 + Math.random() * 0.1) * (1 - eased * 0.6)
        }

        posAttr.needsUpdate = true
        sizeAttr.needsUpdate = true
    })

    return (
        <points ref={points} position={position}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={positions.length / 3}
                    array={positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-color"
                    count={colors.length / 3}
                    array={colors}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-size"
                    count={sizes.length}
                    array={sizes}
                    itemSize={1}
                />
            </bufferGeometry>
            <shaderMaterial
                transparent
                depthWrite={false}
                blending={THREE.AdditiveBlending}
                vertexColors
                vertexShader={`
          attribute float size;
          varying vec3 vColor;
          
          void main() {
            vColor = color;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = size * (180.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `}
                fragmentShader={`
          varying vec3 vColor;
          
          void main() {
            float dist = length(gl_PointCoord - vec2(0.5));
            if (dist > 0.5) discard;
            
            // Clean sharp circle with soft edge
            float alpha = 1.0 - smoothstep(0.35, 0.5, dist);
            
            // Strong glow core
            float glow = 1.0 - smoothstep(0.0, 0.4, dist);
            vec3 color = vColor * (1.0 + glow * 1.5);
            
            gl_FragColor = vec4(color, alpha);
          }
        `}
            />
        </points>
    )
}
